// 五官锁定：把生成图对齐回原照片，再用原图像素覆盖整张脸。
// 模型只负责画头发；脸部（五官、脸型、皮肤）永远来自原照片。

import {
  convexHull,
  dilate,
  erode,
  estimateSimilarity,
  fillPolygon,
  gaussianish,
  IDENTITY,
  isPlausible,
  meanError,
  type Pt,
  type Similarity,
  similarityScale,
} from "./geometry";
import { coverTo, ctx2d, maskToCanvas, newCanvas, resampleMask } from "./image";
import {
  type Analysis,
  analyze,
  FACE_OVAL,
  faceBox,
  LEFT_BROW,
  LEFT_EYE,
  LIPS,
  NOSTRILS,
  pick,
  RIGHT_BROW,
  RIGHT_EYE,
  STABLE_POINTS,
} from "./vision";

export interface LockResult {
  /** 对齐到原图坐标系、皮肤色调已匹配的生成图 */
  aligned: HTMLCanvasElement;
  /** 脸部掩码（已羽化），分辨率 mw×mh */
  mask: Uint8Array;
  mw: number;
  mh: number;
  /** 是否成功锁定了五官 */
  locked: boolean;
  transform: Similarity;
  alignError: number;
}

export async function lockFace(base: HTMLCanvasElement, baseA: Analysis, gen: HTMLCanvasElement): Promise<LockResult> {
  const W = base.width;
  const H = base.height;
  const fitted = gen.width === W && gen.height === H ? gen : coverTo(gen, W, H);

  // 1) 对齐：用稳定关键点求相似变换（只有平移/旋转/等比缩放，不会扭曲）
  let genA = await analyze(fitted);
  let transform = IDENTITY;
  let alignError = 0;
  let aligned = fitted;
  if (baseA.landmarks && genA.landmarks) {
    const src = pick(genA.landmarks, STABLE_POINTS);
    const dst = pick(baseA.landmarks, STABLE_POINTS);
    const t = estimateSimilarity(src, dst);
    const moved = Math.hypot(t.tx, t.ty) > 0.75 || Math.abs(similarityScale(t) - 1) > 0.003 || Math.abs(t.b) > 0.003;
    if (isPlausible(t, W, H) && moved) {
      transform = t;
      alignError = meanError(t, src, dst);
      aligned = warp(fitted, t);
      genA = await analyze(aligned);
    } else {
      alignError = meanError(IDENTITY, src, dst);
    }
  }

  // 2) 脸部候选区：脸型轮廓 ∪ 原图脸部皮肤，去掉原图自己的头发
  const { w: mw, h: mh } = baseA.masks;
  const n = mw * mh;
  const candidate = new Uint8Array(n);
  const inner = new Uint8Array(n);
  const brow = new Uint8Array(n);
  const sx = mw / W;
  const sy = mh / H;
  let faceWidth = mw * 0.3;
  if (baseA.landmarks) {
    const lm = baseA.landmarks;
    const toMask = (pts: Pt[]) => pts.map(([x, y]) => [x * sx, y * sy] as Pt);
    fillPolygon(candidate, mw, mh, toMask(pick(lm, FACE_OVAL)));
    faceWidth = faceBox(lm).width * sx;
    // 五官核心区：眼睛、鼻子、嘴。这里永远用原图，除非新发型明确盖住了它（比如长刘海遮眼）
    fillPolygon(inner, mw, mh, convexHull(toMask(pick(lm, INNER_FACE))));
    // 眉毛区：刘海经常压在这里，头发优先
    fillPolygon(brow, mw, mh, convexHull(toMask(pick(lm, LEFT_BROW))));
    fillPolygon(brow, mw, mh, convexHull(toMask(pick(lm, RIGHT_BROW))));
  }
  const baseSkin = baseA.masks.faceSkin;
  for (let i = 0; i < n; i++) if (baseSkin[i] >= 140) candidate[i] = 255;
  // 原图自己的头发（包括耷拉在额头上的碎发）不能贴回去，否则新发型里会残留旧头发
  const baseHair = dilate(threshold(baseA.masks.hair, 110), mw, mh, 2);
  const innerD = dilate(inner, mw, mh, Math.max(1, Math.round(faceWidth * 0.025)));
  const browD = dilate(brow, mw, mh, Math.max(1, Math.round(faceWidth * 0.03)));

  const genHairRaw = resampleMask(genA.masks.hair, genA.masks.w, genA.masks.h, mw, mh);

  // 3) 先用"确定是脸"的区域做肤色匹配，让生成图的脖子/耳朵和原图同一个光线
  const core = new Uint8Array(n);
  for (let i = 0; i < n; i++) if (candidate[i] && !baseHair[i] && genHairRaw[i] < 100) core[i] = 255;
  matchSkinTone(aligned, base, baseA, genA, core, mw, mh);

  // 4) 差异图：生成图和原图在同一位置差很多 → 模型在这里画了新东西（刘海、碎发），保留生成图
  const diff = diffMap(base, aligned, mw, mh);

  const excludeRaw = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    if (!candidate[i]) continue;
    const hair = genHairRaw[i];
    if (innerD[i]) {
      if (hair >= 200 && diff[i] > DIFF_T) excludeRaw[i] = 255;
    } else if (browD[i]) {
      if (hair >= 90 || (hair >= 40 && diff[i] > DIFF_T * 1.3)) excludeRaw[i] = 255;
    } else if (hair >= 64 || diff[i] > DIFF_T) {
      excludeRaw[i] = 255;
    }
  }
  // 去掉零星噪点，再稍微放大，保证发丝边缘完整
  const exclude = dilate(erode(excludeRaw, mw, mh, 1), mw, mh, 2);

  let raw: Uint8Array = new Uint8Array(n);
  let area = 0;
  for (let i = 0; i < n; i++) {
    if (candidate[i] && !exclude[i] && !baseHair[i]) {
      raw[i] = 255;
      area++;
    }
  }
  raw = erode(raw, mw, mh, 1);
  const feather = Math.max(2, Math.round(faceWidth * 0.035));
  const mask = gaussianish(raw, mw, mh, feather);
  // 发丝软过渡：核心区外，生成图里"有点像头发"的地方按置信度透出新头发，边缘不会像被剪过
  const hairSoft = gaussianish(genHairRaw, mw, mh, 1);
  const coreSoft = gaussianish(innerD, mw, mh, 2);
  for (let i = 0; i < n; i++) {
    const hs = clamp01((hairSoft[i] - 30) / 120) * (1 - coreSoft[i] / 255);
    mask[i] = mask[i] * (1 - hs);
  }
  const locked = area > n * 0.005;

  return { aligned, mask, mw, mh, locked, transform, alignError };
}

/** 平均每通道差值超过这个数，认为模型在这里画了新内容 */
const DIFF_T = 42;

function clamp01(v: number) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** 眼鼻嘴的关键点，凸包即五官核心区（眉毛单独处理） */
const INNER_FACE = [...LEFT_EYE, ...RIGHT_EYE, ...LIPS, ...NOSTRILS, 6, 197, 195, 5, 4, 1];

function diffMap(a: HTMLCanvasElement, b: HTMLCanvasElement, mw: number, mh: number): Uint8Array {
  const small = (src: HTMLCanvasElement) => {
    const c = newCanvas(mw, mh);
    const g = ctx2d(c);
    g.imageSmoothingQuality = "high";
    g.drawImage(src, 0, 0, mw, mh);
    return g.getImageData(0, 0, mw, mh).data;
  };
  const da = small(a);
  const db = small(b);
  const out = new Uint8Array(mw * mh);
  for (let i = 0, j = 0; j < out.length; i += 4, j++) {
    out[j] = (Math.abs(da[i] - db[i]) + Math.abs(da[i + 1] - db[i + 1]) + Math.abs(da[i + 2] - db[i + 2])) / 3;
  }
  return out;
}

function threshold(m: Uint8Array, t: number): Uint8Array {
  const out = new Uint8Array(m.length);
  for (let i = 0; i < m.length; i++) out[i] = m[i] >= t ? 255 : 0;
  return out;
}

function warp(src: HTMLCanvasElement, t: Similarity): HTMLCanvasElement {
  const c = newCanvas(src.width, src.height);
  const g = ctx2d(c);
  g.imageSmoothingQuality = "high";
  // 先铺一层未对齐的，防止边缘露白
  g.drawImage(src, 0, 0);
  g.setTransform(t.a, t.b, -t.b, t.a, t.tx, t.ty);
  g.drawImage(src, 0, 0);
  g.setTransform(1, 0, 0, 1, 0, 0);
  return c;
}

function matchSkinTone(
  aligned: HTMLCanvasElement,
  base: HTMLCanvasElement,
  baseA: Analysis,
  genA: Analysis,
  faceCore: Uint8Array,
  mw: number,
  mh: number,
) {
  const W = base.width;
  const H = base.height;
  const bd = ctx2d(base).getImageData(0, 0, W, H).data;
  const g = ctx2d(aligned);
  const img = g.getImageData(0, 0, W, H);
  const gd = img.data;
  const genSkin = resampleMask(genA.masks.faceSkin, genA.masks.w, genA.masks.h, mw, mh);

  const sum = [0, 0, 0, 0, 0, 0];
  let cnt = 0;
  for (let my = 0; my < mh; my++) {
    for (let mx = 0; mx < mw; mx++) {
      const k = my * mw + mx;
      if (!faceCore[k] || genSkin[k] < 160 || baseA.masks.faceSkin[k] < 160) continue;
      const x = Math.min(W - 1, Math.round((mx + 0.5) * (W / mw)));
      const y = Math.min(H - 1, Math.round((my + 0.5) * (H / mh)));
      const i = (y * W + x) * 4;
      sum[0] += bd[i];
      sum[1] += bd[i + 1];
      sum[2] += bd[i + 2];
      sum[3] += gd[i];
      sum[4] += gd[i + 1];
      sum[5] += gd[i + 2];
      cnt++;
    }
  }
  if (cnt < 30) return;
  const gain = [0, 1, 2].map((c) => Math.min(1.18, Math.max(0.85, sum[c] / Math.max(1, sum[c + 3]))));
  if (gain.every((k) => Math.abs(k - 1) < 0.01)) return;

  // 只作用在生成图的皮肤区域（脸 + 脖子 + 耳朵），羽化过渡
  const skin = new Uint8Array(mw * mh);
  const gb = resampleMask(genA.masks.bodySkin, genA.masks.w, genA.masks.h, mw, mh);
  for (let i = 0; i < skin.length; i++) skin[i] = Math.max(genSkin[i], gb[i]);
  const soft = maskToCanvas(gaussianish(skin, mw, mh, 3), mw, mh, W, H);
  const sd = ctx2d(soft).getImageData(0, 0, W, H).data;
  for (let i = 0; i < gd.length; i += 4) {
    const a = sd[i + 3] / 255;
    if (a <= 0) continue;
    for (let c = 0; c < 3; c++) {
      const v = gd[i + c];
      gd[i + c] = v + (Math.min(255, v * gain[c]) - v) * a;
    }
  }
  g.putImageData(img, 0, 0);
}

/**
 * 合成最终画面：对齐后的生成图打底，脸部区域用 face（原图或淡妆版）覆盖。
 * 用 destination-in 在 GPU 上做蒙版，切换发型时几十毫秒内完成。
 */
export function composite(
  target: HTMLCanvasElement,
  aligned: CanvasImageSource,
  face: HTMLCanvasElement,
  mask: HTMLCanvasElement,
): void {
  const W = face.width;
  const H = face.height;
  if (target.width !== W || target.height !== H) {
    target.width = W;
    target.height = H;
  }
  const layer = newCanvas(W, H);
  const lg = layer.getContext("2d")!;
  lg.drawImage(face, 0, 0);
  lg.globalCompositeOperation = "destination-in";
  lg.drawImage(mask, 0, 0, W, H);
  const g = target.getContext("2d")!;
  g.clearRect(0, 0, W, H);
  g.drawImage(aligned, 0, 0, W, H);
  g.drawImage(layer, 0, 0);
}
